import { equals, ifElse, indexOf, last, map, path, pipe, prop, sortBy, takeLastWhile, takeWhile } from 'ramda';
import { createSelector } from 'reselect';

import { JumpTo, TEntities } from './withItemsReducer.types';
import { MediaManagerSDKAnyItem } from '../../media-manager-sdk';

const getItemEntities = path(['entities', 'items']);
export const getItemIds = prop('items');
export const getSelectedItemIds = prop('selectedItems');

export const getItem = createSelector([getItemEntities, getItemIds], (items) => (id) => items[id]);

export const getItems = createSelector([getItemEntities, getItemIds], (items, ids) =>
  map((id: string) => items[id], ids),
);

export const getSelectedItems = createSelector([getItemEntities, getSelectedItemIds], (items, ids) =>
  map((id: string) => items[id], ids),
);

type TSelectItemToJump = (props: {
  itemId: string;
  items: string[];
  jumpTo: JumpTo;
  entities: TEntities;
}) => MediaManagerSDKAnyItem;

const getElementDomRect = (itemId: string): ClientRect | DOMRect => {
  const domItem = document.querySelector(`[data-hook-item-id="${itemId}"]`);

  if (!domItem) {
    throw new Error(`can't find DOM element with id: ${itemId}`);
  }

  return domItem.getBoundingClientRect();
};

interface IGetItemsUnderOrAboveSelected {
  selectedItemRect: ClientRect | DOMRect;
  jumpTo: JumpTo;
  items: string[];
  index: number;
}

const getCrossedItemsUnderOrAboveSelected = ({
  selectedItemRect,
  jumpTo,
  items,
  index,
}: IGetItemsUnderOrAboveSelected): string[] => {
  const isBottomJump = jumpTo === JumpTo.Bottom;
  const newPossibleItems = [];
  let isFileFound = false;

  const isCrossedItem = (item) => {
    try {
      const { top, bottom, left, right } = getElementDomRect(item);

      const diff = isBottomJump ? top - selectedItemRect.bottom : bottom - selectedItemRect.top;

      const isCrossedItem = left < selectedItemRect.right && right > selectedItemRect.left;

      const isNeededRow = Math.abs(diff) < selectedItemRect.height;

      if (isNeededRow && isCrossedItem) {
        isFileFound = true;

        newPossibleItems.push(item);
        return true;
      }

      return !isFileFound;
    } catch (error) {
      return false;
    }
  };

  if (isBottomJump) {
    takeWhile(isCrossedItem, items.slice(index + 1));
  } else {
    takeLastWhile(isCrossedItem, items.slice(0, index));
  }

  return newPossibleItems;
};

interface IGetItemForSelect {
  selectedItemRect: ClientRect | DOMRect;
  newPossibleItems: string[];
}

interface IWithCoverageSizeItem {
  item: string;
  coverageSize: number;
}

const getItemForSelect = ({ selectedItemRect, newPossibleItems }: IGetItemForSelect): string =>
  pipe<string[], IWithCoverageSizeItem[], IWithCoverageSizeItem[], IWithCoverageSizeItem, string>(
    map((item) => {
      try {
        const { left, right } = getElementDomRect(item);

        if (right <= selectedItemRect.right) {
          return { coverageSize: right - selectedItemRect.left, item };
        }

        if (left >= selectedItemRect.left) {
          return { coverageSize: selectedItemRect.right - left, item };
        }

        return { coverageSize: 0, item };
      } catch (error) {
        return { coverageSize: 0, item };
      }
    }),
    sortBy(prop('coverageSize')),
    last,
    prop('item'),
  )(newPossibleItems);

export const selectItemToJump: TSelectItemToJump = ({ itemId, items, jumpTo, entities }) => {
  const index = indexOf(itemId, items);
  if (jumpTo === JumpTo.Bottom || jumpTo === JumpTo.Top) {
    try {
      const selectedItemRect = getElementDomRect(itemId);

      const newPossibleItems = getCrossedItemsUnderOrAboveSelected({
        selectedItemRect,
        jumpTo,
        items,
        index,
      });

      const countNewPossibleItems = newPossibleItems.length;

      if (!countNewPossibleItems) {
        return entities.items[itemId];
      }

      if (countNewPossibleItems === 1) {
        return entities.items[newPossibleItems[0]];
      }

      return entities.items[getItemForSelect({ newPossibleItems, selectedItemRect }) || itemId];
    } catch (error) {
      return entities.items[itemId];
    }
  }

  const newPossibleItem = ifElse(
    equals(JumpTo.Prev),
    () => (index !== 0 ? entities.items[items[index - 1]] : null),
    () => (index !== items.length - 1 ? entities.items[items[index + 1]] : null),
  )(jumpTo);

  return newPossibleItem || entities.items[itemId];
};
